/**
 * The classes an anchored settings card renders, and the attribute that marks
 * the one a search jump just landed on.
 *
 * A genuine leaf — it imports nothing, so the three card components that need it
 * (`SectionCard`, `EngagementStateCard`, `PaneSection`) do not pull the anchor
 * REGISTRY into their module graph just to draw an outline.
 *
 * Two things live here, and they are two halves of the same behaviour:
 *
 *  - **the focus ring**, because an anchored card is focusable by script only
 *    (`tabIndex={-1}`). Focus that moves somewhere invisible is worse than focus
 *    that did not move;
 *  - **the landing mark**, a static 2px outline that stays for a couple of
 *    seconds after a jump. The browser scrolls the card into view and the ring
 *    only shows while focus is still on it, so on a long page a reader can still
 *    lose the thing they asked for among a dozen identical cards. The mark says
 *    "this one".
 *
 * Static on purpose — no transition, no flash, no smooth scroll — so it reads the
 * same under `prefers-reduced-motion` as it does without it. There is nothing to
 * suppress because nothing moves.
 *
 * The scroll margin is deliberately NOT here. Each card keeps its own: the
 * dashboard cards clear the shell's sticky chrome (`scroll-mt-24`), and the
 * Design Studio's pane sections scroll inside a 22rem inspector column where
 * that much margin would push a section off the top (`scroll-mt-4`).
 */

/** The attribute a just-landed card carries while the mark is showing. */
export const SETTING_LANDED_ATTRIBUTE = 'data-setting-landed';

/** How long the landing mark stays before it clears itself. */
export const SETTING_LANDED_MS = 2_000;

/**
 * Focus ring + landing mark, for any element registered as a setting anchor or
 * as one of its controls.
 */
export const SETTING_ANCHOR_CLASSES =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ' +
  'data-[setting-landed=true]:outline data-[setting-landed=true]:outline-2 data-[setting-landed=true]:outline-offset-2 data-[setting-landed=true]:outline-ring';
